import React, { useEffect } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BoldLabelFormItem } from 'components/form/BoldLabelFormItem';
import { Form, Input, Row, Col, Typography, Select, Radio, Button } from 'antd';
import type { RadioChangeEvent } from 'antd/es/radio';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import TextArea from 'antd/es/input/TextArea';
import { useGetPolicyDrop, useRelationshiplist } from 'all-in-one-shared-api';
import { useKeycloakReady } from 'hooks';
// import { isLocaleEnglish } from 'utils';
import { Spinner } from 'components';
import TrashIcon from 'assets/images/trash.svg';
import addIcon from 'assets/images/addpolicy.svg';

// Base style constants
const inputBaseStyle: React.CSSProperties = {
  border: 'none',
  borderBottom: '1px solid #CCCCCC',
  backgroundColor: 'transparent',
  fontSize: '20px',
  fontFamily: 'Roboto400',
  marginBottom: '10px',
  padding: '4px 0',
  paddingLeft: '11px',
};

const textAreaBaseStyle: React.CSSProperties = {
  fontSize: '20px',
  fontFamily: 'Roboto400',
  fontWeight: 'normal',
  height: '220px',
  marginBottom: '10px',
};

const selectBaseStyle: React.CSSProperties = {
  fontSize: '20px',
  fontFamily: 'Roboto400',
  marginBottom: '10px',
  padding: '4px 0',
};

export const ClaimformForCI = (props: {
  policyNo?: string;
  onSettlementOptionShowAllChange?: (hasAll: boolean) => void;
  onChequeCurrencyOptionsChange?: (options: { value: string; desc?: string; descTC?: string }[]) => void;
}) => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [openList, setOpenList] = useState(false);
  const { keycloak } = useKeycloakReady();
  const [randoms, setRandoms] = useState(Math.random());
  const form = useFormInstance();
  const causedByValue = Form.useWatch('causedBy', form);
  // const isEnglish = isLocaleEnglish(i18n.language);

  const [hospitalList, setHospitalList] = useState<Array<Record<string, unknown>>>([]);
  const [familMemberList, setFamilMemberList] = useState<Array<Record<string, unknown>>>([]);
  const [otherHospitalsList, setOtherHospitalsList] = useState<Array<Record<string, unknown>>>([]);
  const [otherClaimList, setOtherClaimList] = useState<Array<Record<string, unknown>>>([]);

  useEffect(() => {
    const formValues = form.getFieldsValue(true);
    if (formValues.hospitalList?.length) setHospitalList(formValues.hospitalList);
    if (formValues.familMemberList?.length) setFamilMemberList(formValues.familMemberList);
    if (formValues.otherHospitalsList?.length) setOtherHospitalsList(formValues.otherHospitalsList);
    if (formValues.otherClaimList?.length) setOtherClaimList(formValues.otherClaimList);
  }, [form]);

  // 当 causedBy 切换为非 Illness 时，清除 firstConsultationDate 和 nameOfHospital 的校验错误
  useEffect(() => {
    if (causedByValue !== 'Illness') {
      form.setFields([
        { name: 'firstConsultationDate', errors: [] },
        { name: 'nameOfHospital', errors: [] },
      ]);
    }
  }, [causedByValue, form]);

  const syncListFromForm = (
    listName: string,
    stateList: Array<Record<string, unknown>>,
  ): Array<Record<string, unknown>> => {
    const formList = (form.getFieldValue(listName) as Array<Record<string, unknown>>) || [];
    return stateList.map((item, idx) => ({ ...item, ...(formList[idx] || {}) }));
  };

  const onChange2 = (e: Array<string>) => {
    if (policyTypesObject.length == 1 && e.length == 1) {
      setOpenList(false);
    } else {
      setOpenList(true);
    }
    // 检查选中的保单中是否有任何一条的 settlementOptionShow === 'ALL'
    const hasAll = e.some((pno: string) =>
      policyTypesObject.some((item) => item.value === pno && item.settlementOptionShow === 'ALL'),
    );
    props.onSettlementOptionShowAllChange?.(hasAll);

    // 根据所选保单的合约币种构造 chequeCurrencyOptions：最前面插入 HKD 项后按 value 去重
    const hkdOption = {
      value: 'HKD',
      desc: 'HKD',
      descTC: '港元',
    };
    const selectedPolicies = dropData?.filter((item) => e.includes(item.policyNumber)) ?? [];
    const currencyList = selectedPolicies
      .map((item) => {
        return {
          value: item.contractCurrencyCode || item.contractCurrencyDesc,
          desc: item.contractCurrencyDesc,
          descTC: item.contractCurrencyDescTC || item.contractCurrencyDesc,
        };
      })
      .filter((item, index, self) => self.findIndex((i) => i.value === item.value) === index);
    const chequeCurrencyOptions = [hkdOption, ...currencyList.filter((item) => item.value !== hkdOption.value)];
    props.onChequeCurrencyOptionsChange?.(chequeCurrencyOptions);
  };

  const handleHospitalRadioChange = (e: RadioChangeEvent) => {
    const value = e.target.value;
    form.setFieldsValue({ otherHospitalsFiveYearsFlag: value });

    if (value === 'Yes' && hospitalList.length === 0) {
      addHospitalRecord();
    }

    if (value === 'No') {
      setHospitalList([]);
      form.setFieldsValue({ hospitalList: [] });
    }
  };

  const addHospitalRecord = () => {
    const merged = syncListFromForm('hospitalList', hospitalList);
    const nextSortOrder = (merged.length > 0 ? Math.max(...merged.map((r) => (r.sortOrder as number) || 0)) : 0) + 1;
    const newItem = { sortOrder: nextSortOrder, nameOfHospital: '', consultationDate: '' };
    const updated = [...merged, newItem];
    setHospitalList(updated);
    form.setFieldsValue({ hospitalList: updated });
  };

  const deleteHospitalRecord = (sortOrder: number) => {
    const merged = syncListFromForm('hospitalList', hospitalList);
    const updated = merged.filter((r) => r.sortOrder !== sortOrder);
    setHospitalList(updated);
    form.setFieldsValue({ hospitalList: updated });

    if (updated.length === 0) {
      form.setFieldsValue({ otherHospitalsFiveYearsFlag: 'No' });
    }
  };

  const handleFamilyIllnessRadioChange = (e: RadioChangeEvent) => {
    const value = e.target.value;
    form.setFieldsValue({ familMemberSufferedFlag: value });

    if (value === 'Yes' && familMemberList.length === 0) {
      addFamilyIllnessRecord();
    }

    if (value === 'No') {
      setFamilMemberList([]);
      form.setFieldsValue({ familMemberList: [] });
    }
  };

  const addFamilyIllnessRecord = () => {
    const merged = syncListFromForm('familMemberList', familMemberList);
    const nextSortOrder = (merged.length > 0 ? Math.max(...merged.map((r) => (r.sortOrder as number) || 0)) : 0) + 1;
    const newItem = {
      sortOrder: nextSortOrder,
      dateOfDiagnosis: undefined,
      relationshipWithInsured: undefined,
      natureOfIllness: '',
    };
    const updated = [...merged, newItem];
    setFamilMemberList(updated);
    form.setFieldsValue({ familMemberList: updated });
  };

  const deleteFamilyIllnessRecord = (sortOrder: number) => {
    const merged = syncListFromForm('familMemberList', familMemberList);
    const updated = merged.filter((r) => r.sortOrder !== sortOrder);
    setFamilMemberList(updated);
    form.setFieldsValue({ familMemberList: updated });

    if (updated.length === 0) {
      form.setFieldsValue({ familMemberSufferedFlag: 'No' });
    }
  };

  const handlePastMedicalRadioChange = (e: RadioChangeEvent) => {
    const value = e.target.value;
    form.setFieldsValue({ insuredConsultedFlag: value });

    if (value === 'Yes' && otherHospitalsList.length === 0) {
      addPastMedicalRecord();
    }

    if (value === 'No') {
      setOtherHospitalsList([]);
      form.setFieldsValue({ otherHospitalsList: [] });
    }
  };

  const addPastMedicalRecord = () => {
    const merged = syncListFromForm('otherHospitalsList', otherHospitalsList);
    const nextSortOrder = (merged.length > 0 ? Math.max(...merged.map((r) => (r.sortOrder as number) || 0)) : 0) + 1;
    const newItem = { sortOrder: nextSortOrder, nameOfHospital: '', consultationDate: '', illness: '' };
    const updated = [...merged, newItem];
    setOtherHospitalsList(updated);
    form.setFieldsValue({ otherHospitalsList: updated });
  };

  const deletePastMedicalRecord = (sortOrder: number) => {
    const merged = syncListFromForm('otherHospitalsList', otherHospitalsList);
    const updated = merged.filter((r) => r.sortOrder !== sortOrder);
    setOtherHospitalsList(updated);
    form.setFieldsValue({ otherHospitalsList: updated });

    if (updated.length === 0) {
      form.setFieldsValue({ insuredConsultedFlag: 'No' });
    }
  };

  const handleOtherInsuranceRadioChange = (e: RadioChangeEvent) => {
    const value = e.target.value;
    form.setFieldsValue({ otherClaimSubmitFlag: value });

    if (value === 'Yes' && otherClaimList.length === 0) {
      addOtherInsuranceClaim();
    }

    if (value === 'No') {
      setOtherClaimList([]);
      form.setFieldsValue({ otherClaimList: [] });
    }
  };

  const addOtherInsuranceClaim = () => {
    const merged = syncListFromForm('otherClaimList', otherClaimList);
    const nextSortOrder = (merged.length > 0 ? Math.max(...merged.map((r) => (r.sortOrder as number) || 0)) : 0) + 1;
    const newItem = {
      sortOrder: nextSortOrder,
      nameOfInsuranceCompany: '',
      policyNumber: '',
      sumInsured: 'hkd',
      sumInsuredAmount: undefined,
      claimStatus: '',
    };
    const updated = [...merged, newItem];
    setOtherClaimList(updated);
    form.setFieldsValue({ otherClaimList: updated });
  };

  const deleteOtherInsuranceClaim = (sortOrder: number) => {
    const merged = syncListFromForm('otherClaimList', otherClaimList);
    const updated = merged.filter((r) => r.sortOrder !== sortOrder);
    setOtherClaimList(updated);
    form.setFieldsValue({ otherClaimList: updated });

    if (updated.length === 0) {
      form.setFieldsValue({ otherClaimSubmitFlag: 'No' });
    }
  };

  const { shouldFetch: shouldFecthDrop, data: dropData } = useGetPolicyDrop(
    'useGetPolicyDrop' + randoms,
    keycloak.token || '',
  );
  const { data: relationList, shouldFetch: shouldFetchRelation } = useRelationshiplist(
    'useRelationshiplist',
    keycloak.token ?? '',
    '',
  );
  const policyTypesObject: { label: string; label2: string; value: string; settlementOptionShow: string }[] = [];
  dropData?.map((item) => {
    policyTypesObject.push({
      label: i18n.language == 'en' ? item.productName : item.productNameTC,
      label2: `${t('life.enquiry.policyNo')}${item.policyNumber}`,
      value: item.policyNumber,
      settlementOptionShow: item.settlementOptionShow,
    });
  });
  const relationshipOptions: { label: string; labeltc: string; labelen: string; value: string }[] = [];
  relationList?.map((item) => {
    relationshipOptions.push({
      label: `${i18n.language == 'en' ? item.relationshipDesc : item.relationshipDescTc}`,
      labeltc: item.relationshipDescTc,
      labelen: item.relationshipDesc,
      value: item.relationshipCode,
    });
  });
  const causedBy = [
    { label: t('life.submitClaims.illness'), value: 'Illness' },
    { label: t('life.submitClaims.accident'), value: 'Accident' },
  ];

  const currencyOptions = [
    { label: t('life.submitClaims.currencyHkd'), value: 'hkd' },
    { label: t('life.submitClaims.currencyUsd'), value: 'usd' },
    { label: t('life.submitClaims.currencyCny'), value: 'cny' },
  ];

  useEffect(() => {
    setRandoms(Math.random());
    shouldFecthDrop(true);
    shouldFetchRelation(true);
    setIsLoading(true);
  }, [shouldFecthDrop, setIsLoading, shouldFetchRelation]);
  useEffect(() => {
    if (!dropData) return;
    setIsLoading(false);
  }, [dropData, setIsLoading]);

  // 处理初始值：当 dropData 加载完成后，检查初始 policyNumber 中的 settlementOptionShow
  useEffect(() => {
    if (!dropData) return;
    const initialPolicyNo = form.getFieldValue('policyNumber') as string;
    const selectedPolicyNos = initialPolicyNo ? initialPolicyNo.split(',').filter(Boolean) : [];
    const hasAll = selectedPolicyNos.some((pno: string) =>
      policyTypesObject.some((item) => item.value === pno && item.settlementOptionShow === 'ALL'),
    );
    props.onSettlementOptionShowAllChange?.(hasAll);
  }, [dropData]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Row justify="center" gutter={[16, { xs: 8, sm: 8, lg: 24 }]}>
      <Col xs={24} md={24}>
        <Typography.Text className="headertittle">{t('life.submitClaims.informationbelow')}</Typography.Text>
      </Col>
      <Col xs={24} md={24} style={{ marginBottom: '10px' }}>
        <Typography.Text className="headertext">{t('life.MakeClaim.mandatoryMsg')}</Typography.Text>
      </Col>
      {isLoading && <Spinner />}
      <Col xs={24} md={24}>
        <BoldLabelFormItem
          rules={[
            {
              required: true,
              message: t('common.error.requiredField'),
            },
          ]}
          label={
            <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
              {t('life.submitClaims.Selectpolicies')}*
            </span>
          }
          name="policyNumber"
          initialValue={props.policyNo || ''}
          getValueProps={(value: string) => ({
            value: value ? value.split(',').filter(Boolean) : [],
          })}
          normalize={(value: string[]) => value?.join(',') || ''}
        >
          <Select
            mode="multiple"
            popupClassName="slectop"
            placeholder={t('life.beneficiaries.select', { content: t('life.submitClaims.SelectpoliciesPlaceholder') })}
            style={{
              ...selectBaseStyle,
            }}
            open={openList}
            onChange={onChange2}
            onFocus={() => setOpenList(true)}
            onBlur={() => setOpenList(false)}
          >
            {policyTypesObject &&
              policyTypesObject.map((item) => (
                <Select.Option key={item.value} value={item.value} label2={item.label2} title={item.label2}>
                  <div style={{ display: 'grid' }}>
                    <span>{item.label}</span>
                    <span>{item.label2}</span>
                  </div>
                </Select.Option>
              ))}
          </Select>
        </BoldLabelFormItem>
      </Col>

      <Col xs={24} md={24}>
        <BoldLabelFormItem
          rules={[{ required: true, message: t('common.error.requiredField') }]}
          required
          label={
            <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
              {t('life.submitClaims.Causedby')}*
            </span>
          }
          name="causedBy"
        >
          <Select popupClassName="slectop" placeholder={t('life.submitClaims.selectcaused')} style={selectBaseStyle}>
            {causedBy.map((item) => (
              <Select.Option key={item.value} value={item.value}>
                {item.label}
              </Select.Option>
            ))}
          </Select>
        </BoldLabelFormItem>
      </Col>
      {causedByValue === 'Accident' && (
        <Col xs={24} md={24}>
          <BoldLabelFormItem
            rules={[
              {
                required: true,
                message: t('common.error.requiredField'),
              },
            ]}
            required
            label={
              <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
                {t('life.submitClaims.dateOfAccident')}*
              </span>
            }
            name="dateOfAccident"
          >
            <Input size="middle" maxLength={20} placeholder={t('common.placeholder.date')} style={inputBaseStyle} />
          </BoldLabelFormItem>
        </Col>
      )}

      {causedByValue === 'Accident' && (
        <>
          <Col xs={24} md={24}>
            <BoldLabelFormItem
              rules={[{ required: true, message: t('common.error.requiredField') }]}
              required
              label={
                <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
                  {t('life.submitClaims.place')}*
                </span>
              }
              name="place"
            >
              <Input
                size="middle"
                maxLength={50}
                placeholder={t('life.submitClaims.placePlaceholder')}
                style={inputBaseStyle}
              />
            </BoldLabelFormItem>
          </Col>

          <Col xs={24} md={24}>
            <BoldLabelFormItem
              rules={[
                {
                  required: true,
                  message: t('common.error.requiredField'),
                },
              ]}
              required
              label={
                <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
                  {t('life.submitClaims.accidentDetails')}*
                </span>
              }
              name="accidentDetails"
            >
              <TextArea
                showCount
                maxLength={500}
                placeholder={t(`life.submitClaims.accidentDetailsPlaceholder`)}
                style={textAreaBaseStyle}
              />
            </BoldLabelFormItem>
          </Col>
        </>
      )}

      {causedByValue === 'Illness' && (
        <>
          <Col xs={24} md={24}>
            <BoldLabelFormItem
              rules={[
                {
                  required: true,
                  message: t('common.error.requiredField'),
                },
              ]}
              required
              label={
                <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
                  {t('life.submitClaims.dateSymptomsFirstAppeared')}*
                </span>
              }
              name="dateOfSymptoms"
            >
              <Input size="middle" maxLength={20} placeholder={t('common.placeholder.date')} style={inputBaseStyle} />
            </BoldLabelFormItem>
          </Col>

          <Col xs={24} md={24}>
            <BoldLabelFormItem
              rules={[
                {
                  required: true,
                  message: t('common.error.requiredField'),
                },
              ]}
              required
              label={
                <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
                  {t('life.submitClaims.symptomsDetails')}*
                </span>
              }
              name="symptomsDetails"
            >
              <TextArea
                showCount
                maxLength={500}
                placeholder={t(`life.submitClaims.symptomsDetailsPlaceholder`)}
                style={textAreaBaseStyle}
              />
            </BoldLabelFormItem>
          </Col>
        </>
      )}

      {causedByValue && (
        <>
          <Col xs={24} md={24}>
            <BoldLabelFormItem
              rules={
                causedByValue === 'Illness'
                  ? [
                      {
                        required: true,
                        message: t('common.error.requiredField'),
                      },
                    ]
                  : []
              }
              required={causedByValue === 'Illness'}
              label={
                <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
                  {t('life.submitClaims.firstConsultationDate')}
                  {causedByValue === 'Illness' && '*'}
                </span>
              }
              name="firstConsultationDate"
            >
              <Input size="middle" maxLength={20} placeholder={t('common.placeholder.date')} style={inputBaseStyle} />
            </BoldLabelFormItem>
          </Col>
          <Col xs={24} md={24}>
            <BoldLabelFormItem
              rules={
                causedByValue === 'Illness'
                  ? [
                      {
                        required: true,
                        message: t('common.error.requiredField'),
                      },
                    ]
                  : []
              }
              required={causedByValue === 'Illness'}
              label={
                <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
                  {t('life.submitClaims.nameOfHospital')}
                  {causedByValue === 'Illness' && '*'}
                </span>
              }
              name="nameOfHospital"
            >
              <TextArea
                showCount
                maxLength={500}
                placeholder={t(`life.submitClaims.nameOfHospitalPlaceholder`)}
                style={{ ...textAreaBaseStyle, width: '100%' }}
              />
            </BoldLabelFormItem>
          </Col>
        </>
      )}
      <Col xs={24} md={24}>
        <BoldLabelFormItem
          rules={[
            {
              required: true,
              message: t('common.error.requiredField'),
            },
          ]}
          required
          label={
            <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
              {t('life.submitClaims.consultedOtherHospitals')}*
            </span>
          }
          name="otherHospitalsFiveYearsFlag"
        >
          <Radio.Group
            onChange={handleHospitalRadioChange}
            style={{
              fontSize: '20px',
              fontFamily: 'Roboto400',
            }}
          >
            <Radio value="Yes" style={{ marginRight: '20px' }}>
              {t('common.yes')}
            </Radio>
            <Radio value="No">{t('common.no')}</Radio>
          </Radio.Group>
        </BoldLabelFormItem>
      </Col>

      {/* Hospital records section - show only if yes is selected and there are records */}
      {hospitalList.length > 0 && (
        <div
          style={{
            marginBottom: '20px',
            border: '1px solid #e8e8e8',
            padding: '16px',
            borderRadius: '4px',
            width: '100%',
          }}
        >
          {hospitalList.map((record: Record<string, unknown>, index: number) => (
            <div key={record.sortOrder as number} style={{ marginBottom: '20px' }}>
              <Col xs={24} md={24}>
                <BoldLabelFormItem
                  rules={[
                    {
                      required: true,
                      message: t('common.error.requiredField'),
                    },
                  ]}
                  required
                  label={
                    <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
                      {t('life.submitClaims.nameOfHospitalPhysicianAndAddress')}*
                    </span>
                  }
                  name={[`hospitalList`, index, 'nameOfHospital']}
                >
                  <TextArea
                    showCount
                    maxLength={500}
                    placeholder={t(`life.submitClaims.hospitalNameAddressPlaceholder`)}
                    style={{ ...textAreaBaseStyle, width: '100%' }}
                  />
                </BoldLabelFormItem>
              </Col>

              <Col xs={24} md={24}>
                <BoldLabelFormItem
                  rules={[
                    {
                      required: true,
                      message: t('common.error.requiredField'),
                    },
                  ]}
                  required
                  label={
                    <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
                      {t('life.submitClaims.consultationDate')}*
                    </span>
                  }
                  name={[`hospitalList`, index, 'consultationDate']}
                >
                  <Input
                    size="middle"
                    maxLength={20}
                    placeholder={t('common.placeholder.date')}
                    style={{ ...inputBaseStyle, width: '100%' }}
                  />
                </BoldLabelFormItem>
              </Col>

              {/* Action buttons row - per record */}
              <Col
                xs={24}
                md={24}
                style={{ marginTop: '10px', display: 'flex', gap: '10px', justifyContent: 'center' }}
              >
                <Button
                  type="text"
                  onClick={() => {
                    deleteHospitalRecord(record.sortOrder as number);
                  }}
                  style={{
                    fontSize: '16px',
                    fontFamily: 'Roboto400',
                    color: '#666666',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={TrashIcon}
                    alt="Delete"
                    style={{ marginRight: '8px', width: '16px', height: '16px', verticalAlign: 'middle' }}
                  />
                  {t('life.submitClaims.delete')}
                </Button>

                <Button
                  type="text"
                  onClick={addHospitalRecord}
                  style={{
                    fontSize: '16px',
                    fontFamily: 'Roboto400',
                    color: '#752127',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={addIcon}
                    alt="Add"
                    style={{ marginRight: '8px', width: '16px', height: '16px', verticalAlign: 'middle' }}
                  />
                  {t('life.submitClaims.addANewRecord')}
                </Button>
              </Col>
              {/* Add separator between records except last one */}
              {index < hospitalList.length - 1 && (
                <div style={{ height: '1px', backgroundColor: '#e8e8e8', margin: '20px 0' }}></div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Family illness section */}
      <Col xs={24} md={24}>
        <BoldLabelFormItem
          rules={[
            {
              required: true,
              message: t('common.error.requiredField'),
            },
          ]}
          required
          label={
            <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
              {t('life.submitClaims.haveFamilyIllness')}*
            </span>
          }
          name="familMemberSufferedFlag"
        >
          <Radio.Group
            onChange={handleFamilyIllnessRadioChange}
            style={{
              fontSize: '20px',
              fontFamily: 'Roboto400',
            }}
          >
            <Radio value="Yes" style={{ marginRight: '20px' }}>
              {t('common.yes')}
            </Radio>
            <Radio value="No">{t('common.no')}</Radio>
          </Radio.Group>
        </BoldLabelFormItem>
      </Col>

      {/* Family illness records section - show only if yes is selected and there are records */}
      {familMemberList.length > 0 && (
        <div
          style={{
            marginBottom: '20px',
            border: '1px solid #e8e8e8',
            padding: '16px',
            borderRadius: '4px',
            width: '100%',
          }}
        >
          {familMemberList.map((record: Record<string, unknown>, index: number) => (
            <div key={record.sortOrder as number} style={{ marginBottom: '20px' }}>
              <Col xs={24} md={24}>
                <BoldLabelFormItem
                  rules={[
                    {
                      required: true,
                      message: t('common.error.requiredField'),
                    },
                  ]}
                  required
                  label={
                    <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
                      {t('life.submitClaims.dateOfDiagnosis')}*
                    </span>
                  }
                  name={[`familMemberList`, index, 'dateOfDiagnosis']}
                >
                  <Input
                    size="middle"
                    maxLength={20}
                    placeholder={t('common.placeholder.date')}
                    style={{ ...inputBaseStyle, width: '100%' }}
                  />
                </BoldLabelFormItem>
              </Col>

              <Col xs={24} md={24}>
                <BoldLabelFormItem
                  rules={[
                    {
                      required: true,
                      message: t('common.error.requiredField'),
                    },
                  ]}
                  required
                  label={
                    <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
                      {t('life.submitClaims.relationshipWithInsured')}*
                    </span>
                  }
                  name={[`familMemberList`, index, 'relationshipWithInsured']}
                >
                  <Select
                    popupClassName="slectop"
                    placeholder={t('life.submitClaims.selectRelationship')}
                    style={selectBaseStyle}
                  >
                    {relationshipOptions.map((item) => (
                      <Select.Option key={item.value} value={item.value}>
                        {item.label}
                      </Select.Option>
                    ))}
                  </Select>
                </BoldLabelFormItem>
              </Col>

              <Col xs={24} md={24}>
                <BoldLabelFormItem
                  rules={[
                    {
                      required: true,
                      message: t('common.error.requiredField'),
                    },
                  ]}
                  required
                  label={
                    <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
                      {t('life.submitClaims.natureOfIllness')}*
                    </span>
                  }
                  name={[`familMemberList`, index, 'natureOfIllness']}
                >
                  <TextArea
                    showCount
                    maxLength={500}
                    placeholder={t(`life.submitClaims.illnessNaturePlaceholder`)}
                    style={{ ...textAreaBaseStyle, width: '100%' }}
                  />
                </BoldLabelFormItem>
              </Col>

              {/* Action buttons row - per record */}
              <Col
                xs={24}
                md={24}
                style={{ marginTop: '10px', display: 'flex', gap: '10px', justifyContent: 'center' }}
              >
                <Button
                  type="text"
                  onClick={() => {
                    deleteFamilyIllnessRecord(record.sortOrder as number);
                  }}
                  style={{
                    fontSize: '16px',
                    fontFamily: 'Roboto400',
                    color: '#666666',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={TrashIcon}
                    alt="Delete"
                    style={{ marginRight: '8px', width: '16px', height: '16px', verticalAlign: 'middle' }}
                  />
                  {t('life.submitClaims.delete')}
                </Button>

                <Button
                  type="text"
                  onClick={addFamilyIllnessRecord}
                  style={{
                    fontSize: '16px',
                    fontFamily: 'Roboto400',
                    color: '#752127',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={addIcon}
                    alt="Add"
                    style={{ marginRight: '8px', width: '16px', height: '16px', verticalAlign: 'middle' }}
                  />
                  {t('life.submitClaims.addANewRecord')}
                </Button>
              </Col>
              {/* Add separator between records except last one */}
              {index < familMemberList.length - 1 && (
                <div style={{ height: '1px', backgroundColor: '#e8e8e8', margin: '20px 0' }}></div>
              )}
            </div>
          ))}
        </div>
      )}
      {/* Past 5 years medical history section */}
      <Col xs={24} md={24}>
        <BoldLabelFormItem
          rules={[
            {
              required: true,
              message: t('common.error.requiredField'),
            },
          ]}
          required
          label={
            <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
              {t('life.submitClaims.pastMedicalHistoryQuestion')}*
            </span>
          }
          name="insuredConsultedFlag"
        >
          <Radio.Group
            onChange={handlePastMedicalRadioChange}
            style={{
              fontSize: '20px',
              fontFamily: 'Roboto400',
            }}
          >
            <Radio value="Yes" style={{ marginRight: '20px' }}>
              {t('common.yes')}
            </Radio>
            <Radio value="No">{t('common.no')}</Radio>
          </Radio.Group>
        </BoldLabelFormItem>
      </Col>

      {/* Past medical records section - show only if yes is selected and there are records */}
      {otherHospitalsList.length > 0 && (
        <div
          style={{
            marginBottom: '20px',
            border: '1px solid #e8e8e8',
            padding: '16px',
            borderRadius: '4px',
            width: '100%',
          }}
        >
          {otherHospitalsList.map((record: Record<string, unknown>, index: number) => (
            <div key={record.sortOrder as number} style={{ marginBottom: '20px' }}>
              <Col xs={24} md={24}>
                <BoldLabelFormItem
                  rules={[
                    {
                      required: true,
                      message: t('common.error.requiredField'),
                    },
                  ]}
                  required
                  label={
                    <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
                      {t('life.submitClaims.nameOfHospitalPhysicianAndAddress')}*
                    </span>
                  }
                  name={[`otherHospitalsList`, index, 'nameOfHospital']}
                >
                  <TextArea
                    showCount
                    maxLength={500}
                    placeholder={t(`life.submitClaims.hospitalNameAddressPlaceholder`)}
                    style={{ ...textAreaBaseStyle, width: '100%' }}
                  />
                </BoldLabelFormItem>
              </Col>

              <Col xs={24} md={24}>
                <BoldLabelFormItem
                  rules={[
                    {
                      required: true,
                      message: t('common.error.requiredField'),
                    },
                  ]}
                  required
                  label={
                    <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
                      {t('life.submitClaims.consultationDate')}*
                    </span>
                  }
                  name={[`otherHospitalsList`, index, 'consultationDate']}
                >
                  <Input
                    size="middle"
                    maxLength={20}
                    placeholder={t('common.placeholder.date')}
                    style={{ ...inputBaseStyle, width: '100%' }}
                  />
                </BoldLabelFormItem>
              </Col>

              <Col xs={24} md={24}>
                <BoldLabelFormItem
                  rules={[
                    {
                      required: true,
                      message: t('common.error.requiredField'),
                    },
                  ]}
                  required
                  label={
                    <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
                      {t('life.submitClaims.illnessDiagnosis')}*
                    </span>
                  }
                  name={[`otherHospitalsList`, index, 'illness']}
                >
                  <TextArea
                    showCount
                    maxLength={500}
                    placeholder={t(`life.submitClaims.illnessDiagnosisPlaceholder`)}
                    style={{ ...textAreaBaseStyle, width: '100%' }}
                  />
                </BoldLabelFormItem>
              </Col>

              {/* Action buttons row - per record */}
              <Col
                xs={24}
                md={24}
                style={{ marginTop: '10px', display: 'flex', gap: '10px', justifyContent: 'center' }}
              >
                <Button
                  type="text"
                  onClick={() => {
                    deletePastMedicalRecord(record.sortOrder as number);
                  }}
                  style={{
                    fontSize: '16px',
                    fontFamily: 'Roboto400',
                    color: '#666666',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={TrashIcon}
                    alt="Delete"
                    style={{ marginRight: '8px', width: '16px', height: '16px', verticalAlign: 'middle' }}
                  />
                  {t('life.submitClaims.delete')}
                </Button>

                <Button
                  type="text"
                  onClick={addPastMedicalRecord}
                  style={{
                    fontSize: '16px',
                    fontFamily: 'Roboto400',
                    color: '#752127',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={addIcon}
                    alt="Add"
                    style={{ marginRight: '8px', width: '16px', height: '16px', verticalAlign: 'middle' }}
                  />
                  {t('life.submitClaims.addANewRecord')}
                </Button>
              </Col>
              {/* Add separator between records except last one */}
              {index < otherHospitalsList.length - 1 && (
                <div style={{ height: '1px', backgroundColor: '#e8e8e8', margin: '20px 0' }}></div>
              )}
            </div>
          ))}
        </div>
      )}
      {/* Other insurance claims section */}
      <Col xs={24} md={24}>
        <BoldLabelFormItem
          rules={[
            {
              required: true,
              message: t('common.error.requiredField'),
            },
          ]}
          required
          label={
            <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
              {t('life.submitClaims.otherInsuranceClaimQuestion')}*
            </span>
          }
          name="otherClaimSubmitFlag"
        >
          <Radio.Group
            onChange={handleOtherInsuranceRadioChange}
            style={{
              fontSize: '20px',
              fontFamily: 'Roboto400',
            }}
          >
            <Radio value="Yes" style={{ marginRight: '20px' }}>
              {t('common.yes')}
            </Radio>
            <Radio value="No">{t('common.no')}</Radio>
          </Radio.Group>
        </BoldLabelFormItem>
      </Col>

      {/* Other insurance claims records section - show only if yes is selected and there are records */}
      {otherClaimList.length > 0 && (
        <div
          style={{
            marginBottom: '20px',
            border: '1px solid #e8e8e8',
            padding: '16px',
            borderRadius: '4px',
            width: '100%',
          }}
        >
          {otherClaimList.map((record: Record<string, unknown>, index: number) => (
            <div key={record.sortOrder as number} style={{ marginBottom: '20px' }}>
              <Col xs={24} md={24}>
                <BoldLabelFormItem
                  rules={[
                    {
                      required: true,
                      message: t('common.error.requiredField'),
                    },
                  ]}
                  required
                  label={
                    <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
                      {t('life.submitClaims.nameOfInsuranceCompany')}*
                    </span>
                  }
                  name={[`otherClaimList`, index, 'nameOfInsuranceCompany']}
                >
                  <Input
                    size="middle"
                    placeholder={t(`life.submitClaims.insuranceCompanyNamePlaceholder`)}
                    style={{ ...inputBaseStyle, width: '100%' }}
                    maxLength={50}
                  />
                </BoldLabelFormItem>
              </Col>

              <Col xs={24} md={24}>
                <BoldLabelFormItem
                  rules={[
                    {
                      required: true,
                      message: t('common.error.requiredField'),
                    },
                  ]}
                  required
                  label={
                    <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
                      {t('life.submitClaims.policyNumber')}*
                    </span>
                  }
                  name={[`otherClaimList`, index, 'policyNumber']}
                >
                  <Input
                    size="middle"
                    placeholder={t(`life.submitClaims.policyNumberPlaceholder`)}
                    style={{ ...inputBaseStyle, width: '100%' }}
                    maxLength={20}
                  />
                </BoldLabelFormItem>
              </Col>

              <Col xs={24} md={24}>
                <BoldLabelFormItem
                  rules={[
                    {
                      required: true,
                      message: t('common.error.requiredField'),
                    },
                  ]}
                  required
                  label={
                    <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
                      {t('life.submitClaims.sumInsured')}*
                    </span>
                  }
                >
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <Form.Item
                      name={[`otherClaimList`, index, 'sumInsured']}
                      style={{ flex: 1, marginBottom: 0 }}
                      initialValue="hkd"
                    >
                      <Select
                        style={{
                          ...selectBaseStyle,
                          border: 'none',
                          borderBottom: '1px solid #CCCCCC',
                          backgroundColor: 'transparent',
                        }}
                        className="currency-select"
                      >
                        {currencyOptions.map((item) => (
                          <Select.Option key={item.value} value={item.value}>
                            {item.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      name={[`otherClaimList`, index, 'sumInsuredAmount']}
                      rules={[
                        {
                          required: true,
                          message: t('common.error.requiredField'),
                        },
                        { pattern: /^\d+(\.\d{1,2})?$/, message: t('life.submitClaims.onlyNumbersAllowed') },
                      ]}
                      style={{ flex: 2, marginBottom: 0 }}
                    >
                      <Input
                        size="middle"
                        placeholder={t(`life.submitClaims.sumInsuredAmountPlaceholder`)}
                        style={{ ...inputBaseStyle, paddingLeft: '11px' }}
                        maxLength={10}
                        onChange={(e) => {
                          const rawValue = e.target.value;
                          const sanitized = rawValue.replace(/[^0-9.]/g, '').replace(/(\.\d{0,2}).*$/, '$1');
                          form.setFieldsValue({
                            otherClaimList: form
                              .getFieldValue('otherClaimList')
                              .map((item: Record<string, unknown>, i: number) =>
                                i === index ? { ...item, sumInsuredAmount: sanitized } : item,
                              ),
                          });
                        }}
                      />
                    </Form.Item>
                  </div>
                </BoldLabelFormItem>
              </Col>

              <Col xs={24} md={24}>
                <BoldLabelFormItem
                  rules={[
                    {
                      required: true,
                      message: t('common.error.requiredField'),
                    },
                  ]}
                  required
                  label={
                    <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
                      {t('life.submitClaims.claimStatus')}*
                    </span>
                  }
                  name={[`otherClaimList`, index, 'claimStatus']}
                >
                  <Input
                    size="middle"
                    placeholder={t(`life.submitClaims.claimStatusPlaceholder`)}
                    style={{ ...inputBaseStyle, width: '100%' }}
                    maxLength={20}
                  />
                </BoldLabelFormItem>
              </Col>

              {/* Action buttons row - per record */}
              <Col
                xs={24}
                md={24}
                style={{ marginTop: '10px', display: 'flex', gap: '10px', justifyContent: 'center' }}
              >
                <Button
                  type="text"
                  onClick={() => {
                    deleteOtherInsuranceClaim(record.sortOrder as number);
                  }}
                  style={{
                    fontSize: '16px',
                    fontFamily: 'Roboto400',
                    color: '#666666',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={TrashIcon}
                    alt="Delete"
                    style={{ marginRight: '8px', width: '16px', height: '16px', verticalAlign: 'middle' }}
                  />
                  {t('life.submitClaims.delete')}
                </Button>

                <Button
                  type="text"
                  onClick={addOtherInsuranceClaim}
                  style={{
                    fontSize: '16px',
                    fontFamily: 'Roboto400',
                    color: '#752127',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={addIcon}
                    alt="Add"
                    style={{ marginRight: '8px', width: '16px', height: '16px', verticalAlign: 'middle' }}
                  />
                  {t('life.submitClaims.addANewRecord')}
                </Button>
              </Col>
              {/* Add separator between records except last one */}
              {index < otherClaimList.length - 1 && (
                <div style={{ height: '1px', backgroundColor: '#e8e8e8', margin: '20px 0' }}></div>
              )}
            </div>
          ))}
        </div>
      )}
    </Row>
  );
};
